import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { cn } from "../lib/utils";

const BASE = "/underwriting-exceptions";

export interface HiddenSub {
  letter: string;
  label: string;
  to: string;
}

export interface Step {
  id: string;
  index: number;
  label: string;
  to: string;
  hiddenSubs?: HiddenSub[];
}

export const STEPS: Step[] = [
  {
    id: "underwriting-health",
    index: 1,
    label: "Underwriting Health",
    to: BASE,
    hiddenSubs: [
      { letter: "a", label: "Manage Alerts", to: `${BASE}/manage-alerts` },
      { letter: "b", label: "Manage Office Rollout", to: `${BASE}/manage-office-rollout` },
    ],
  },
  { id: "productivity", index: 2, label: "Productivity", to: `${BASE}/productivity` },
  {
    id: "exceptions-overrides",
    index: 3,
    label: "Exceptions and Overrides",
    to: `${BASE}/exceptions-overrides`,
  },
  {
    id: "policy-compliance",
    index: 4,
    label: "Policy Compliance by Rule",
    to: `${BASE}/policy-compliance`,
  },
  {
    id: "exceptions-log",
    index: 5,
    label: "Deal-Level Exceptions Log",
    to: `${BASE}/exceptions-log`,
  },
  {
    id: "dealer-quality",
    index: 6,
    label: "Dealer and Channel Quality",
    to: `${BASE}/dealer-quality`,
  },
  {
    id: "agreement-culture",
    index: 7,
    label: "Agreement Rate and Credit Culture",
    to: `${BASE}/agreement-culture`,
  },
];

export function getStep(id: string): Step {
  const step = STEPS.find((s) => s.id === id);
  if (!step) throw new Error(`Unknown step: ${id}`);
  return step;
}

const allSubs = STEPS.flatMap((s) => s.hiddenSubs ?? []);

function StepNav() {
  const { pathname } = useLocation();
  const onSubStep = allSubs.some((sub) => sub.to === pathname);
  const [expanded, setExpanded] = useState(onSubStep);
  const showSubs = expanded || onSubStep;

  return (
    <nav aria-label="Report steps" className="flex flex-wrap items-center gap-2">
      {STEPS.map((step) => {
        const isActive = step.to === BASE ? pathname === BASE || pathname === `${BASE}/` : pathname === step.to;
        const subs = step.hiddenSubs ?? [];
        return (
          <div key={step.id} className="flex flex-wrap items-center gap-2">
            <Link
              to={step.to}
              className={cn(
                "group inline-flex items-center gap-2 border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/50 hover:text-foreground",
                isActive ? "border-primary bg-amber-dim text-primary" : "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "grid size-4 place-items-center bg-secondary text-[0.6rem] font-semibold tabular-nums text-foreground",
                  isActive && "bg-primary text-primary-foreground",
                )}
              >
                {step.index}
              </span>
              {step.label}
            </Link>

            {subs.length > 0 ? (
              <>
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={showSubs}
                  aria-label={showSubs ? "Hide sub-steps" : "Show sub-steps"}
                  className="grid size-6 place-items-center border border-border bg-card text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                >
                  {showSubs ? <Minus className="size-3" /> : <Plus className="size-3" />}
                </button>
                {showSubs
                  ? subs.map((sub) => {
                      const subActive = pathname === sub.to;
                      return (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className={cn(
                            "inline-flex items-center gap-2 border border-dashed border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/50 hover:text-foreground",
                            subActive ? "border-primary bg-amber-dim text-primary" : "text-muted-foreground",
                          )}
                        >
                          <span className="grid size-4 place-items-center bg-secondary text-[0.6rem] font-semibold lowercase">
                            {sub.letter}
                          </span>
                          {sub.label}
                        </Link>
                      );
                    })
                  : null}
              </>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

// The step nav and content are full-bleed with a 24px gutter, matching Risk Based
// Pricing's `main className="p-6"`. Both were `mx-auto max-w-7xl px-4 sm:px-6`, which
// left a wide empty margin either side above 1280px while RBP filled the width. The
// two fixed footers below stay centered at max-w-7xl — that matches RBP too, whose
// shared StepsFooter centers its own row.
function Shell({ children, nav }: { children: ReactNode; nav?: ReactNode }) {
  return (
    <div className="min-h-screen bg-background pt-[110px]">
      <div className="border-b border-border bg-card/40">
        <div className="px-6 py-4">
          <StepNav />
        </div>
      </div>
      <main className="space-y-6 px-6 py-8 pb-24">{children}</main>
      {nav}
    </div>
  );
}

export function StepShell({ step, children }: { step: Step; children: ReactNode }) {
  const navigate = useNavigate();
  const { prev, next } = useMemo(() => {
    const i = STEPS.findIndex((s) => s.id === step.id);
    return { prev: STEPS[i - 1], next: STEPS[i + 1] };
  }, [step.id]);

  return (
    <Shell
      nav={
        <footer
          className="fixed bottom-0 left-0 z-50 px-6 py-2"
          style={{
            backgroundColor: "#191e27",
            right: "var(--rightNavWidth, 0px)",
            transition: "right 0.2s ease",
          }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Step {step.index} of {STEPS.length}
            </span>
            <div className="flex items-center gap-4">
              {prev ? (
                <button
                  type="button"
                  onClick={() => navigate(prev.to)}
                  className="min-w-[100px] border border-border px-6 py-2 font-medium text-foreground hover:border-primary"
                >
                  {prev.label}
                </button>
              ) : (
                <div className="min-w-[100px] px-6 py-2" />
              )}
              {next ? (
                <button
                  type="button"
                  onClick={() => navigate(next.to)}
                  className="min-w-[100px] bg-primary px-6 py-2 font-medium text-primary-foreground hover:bg-primary/90"
                >
                  {next.label}
                </button>
              ) : (
                <div className="min-w-[100px] px-6 py-2" />
              )}
            </div>
          </div>
        </footer>
      }
    >
      {children}
    </Shell>
  );
}

export function SubStepShell({
  subStepId,
  children,
  backTo = BASE,
  backLabel = "Underwriting Health",
}: {
  subStepId: string;
  children: ReactNode;
  backTo?: string;
  backLabel?: string;
}) {
  const sub = allSubs.find((s) => s.to === `${BASE}/${subStepId}` || s.label === subStepId);

  return (
    <Shell
      nav={
        <footer
          className="fixed bottom-0 left-0 z-50 px-6 py-2"
          style={{
            backgroundColor: "#191e27",
            right: "var(--rightNavWidth, 0px)",
            transition: "right 0.2s ease",
          }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between">
            <Link
              to={backTo}
              className="inline-flex min-w-[100px] items-center gap-2 border border-border px-6 py-2 font-medium text-foreground hover:border-primary"
            >
              <ChevronLeft className="size-4" />
              {backLabel}
            </Link>
            {sub ? (
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Sub-step {sub.letter} · {sub.label}
              </span>
            ) : null}
          </div>
        </footer>
      }
    >
      {children}
    </Shell>
  );
}
